import { Popover } from '@cowbell/ui/core';
import { faGlobe, faGrid2 } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, type ReactNode } from 'react';
import { isProviderUsable } from '../../types/connected-apps';
import { useConnectedApps } from './connected-apps-context';
import {
  ConnectedAppsPopover,
  type WebSearchToggle,
} from './ConnectedAppsPopover';
import styles from './ConnectedAppsChipStrip.module.css';

/**
 * The composer's one "Apps" control: how many of the caller's apps the agent
 * can use this turn, a globe while web search is on, and a popover to review
 * apps, tone and web search. A single quiet pill rather than a strip of brand
 * logos, which competed with the message for attention.
 *
 * Reads a host-filled context (see `ConnectedAppsProvider`) rather than
 * fetching, because this library cannot reach the console's data layer.
 * Renders nothing when the tenant has enabled no apps, so hosts that never
 * mount the provider are unaffected.
 */
export interface ConnectedAppsChipStripProps {
  /** Host-supplied controls shown at the top of the popover — the response
   *  tone selector lives here rather than on the action row, where it took
   *  prime space for a rarely-used control. */
  readonly toneSlot?: ReactNode;
  /** Library-owned web-search switch state. Rendered only when the host's
   *  context reports `webSearchAvailable`. */
  readonly webSearch?: WebSearchToggle;
}

export function ConnectedAppsChipStrip({
  toneSlot,
  webSearch,
}: ConnectedAppsChipStripProps = {}) {
  const {
    providers,
    renderIcon,
    onManage,
    onConnect,
    onSetEnabled,
    webSearchAvailable,
  } = useConnectedApps();
  const [opened, setOpened] = useState(false);
  const webToggle = webSearchAvailable ? webSearch : undefined;
  // A visible mark whenever queries may leave for the web this turn.
  const webOn = Boolean(webToggle?.enabled);

  // The popover also hosts composer settings (tone, web search), so it must
  // survive a host that mounts no connected apps — otherwise those controls
  // disappear with it.
  if (providers.length === 0 && !toneSlot && !webToggle) return null;

  const usable = providers.filter(isProviderUsable);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={360}
      position="top-start"
      shadow="md"
      withArrow
      zIndex={1300}
      floatingStrategy="fixed"
      classNames={{ dropdown: styles.dropdown }}
    >
      <Popover.Target>
        <button
          type="button"
          className={styles.chip}
          onClick={() => setOpened((o) => !o)}
          aria-label={`Apps — connected apps: using ${usable.length} of ${
            providers.length
          }${webOn ? ', web search on' : ''}`}
        >
          <FontAwesomeIcon icon={faGrid2} className={styles.settingsIcon} />
          <span className={styles.label}>Apps</span>
          {usable.length > 0 && (
            <span className={styles.count}>{usable.length}</span>
          )}
          {webOn && (
            <span
              className={styles.webMark}
              data-testid="web-search-mark"
              title="Web search is on: the agent may look up public web pages this turn"
            >
              <FontAwesomeIcon icon={faGlobe} />
            </span>
          )}
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <ConnectedAppsPopover
          providers={providers}
          renderIcon={renderIcon}
          toneSlot={toneSlot}
          webSearch={webToggle}
          onSetEnabled={onSetEnabled}
          onManage={
            onManage &&
            (() => {
              setOpened(false);
              onManage();
            })
          }
          onConnect={
            onConnect &&
            ((provider) => {
              setOpened(false);
              onConnect(provider);
            })
          }
        />
      </Popover.Dropdown>
    </Popover>
  );
}
